"use client";

import { useCallback, useMemo, useState } from "react";

import {
  convertToAssetId,
  isZapAsset,
  trpc,
  useMoveAssetsToFolder,
  useSetPinnedCanvas,
} from "@zapier/client";
import { useTranslations } from "@zapier/i18n";
import { AssetTypes, useNotification } from "@zapier/utils";

import type { HydratedAsset } from "@zapier/client";

interface TypedAssetIds {
  zapIds: string[];
  tableIds: string[];
  interfaceIds: string[];
  chatbotIds: string[];
  agentIds: string[];
}

const CANVAS_GENERATE_ENDPOINT = "/api/canvasui/folder/generate";

/** asset.id for zaps is "zap:123"; zsl-api expects the numeric part only. */
function getZapIdForZsl(assetId: string): string {
  const afterPrefix = assetId.startsWith("zap:") ? assetId.slice(4) : assetId;
  return afterPrefix;
}

function buildTypedIds(assets: HydratedAsset[]): TypedAssetIds {
  const typed: TypedAssetIds = {
    zapIds: [],
    tableIds: [],
    interfaceIds: [],
    chatbotIds: [],
    agentIds: [],
  };

  for (const asset of assets) {
    switch (asset.type) {
      case AssetTypes.Zap: {
        if (!isZapAsset(asset)) break;
        // zsl-api expects numeric zap id; use draft.zapId, fallback to id part of asset.id (strip "zap:" prefix).
        const zapIdFromDraft = asset.draft?.zapId;
        const zapId = zapIdFromDraft ?? getZapIdForZsl(asset.id);
        typed.zapIds.push(String(zapId));
        break;
      }
      case AssetTypes.Table: {
        typed.tableIds.push(asset.id);
        break;
      }
      case AssetTypes.Interface: {
        typed.interfaceIds.push(asset.id);
        break;
      }
      case AssetTypes.Chatbot: {
        typed.chatbotIds.push(asset.id);
        break;
      }
      case AssetTypes.Agent: {
        typed.agentIds.push(asset.id);
        break;
      }
      default: {
        break;
      }
    }
  }

  return typed;
}

export function useGenerateCanvasFromFolderAssets({
  folderId,
  folderName,
  assets,
  pinnedCanvasId,
}: {
  folderId: string;
  folderName?: string | null;
  assets: HydratedAsset[];
  pinnedCanvasId?: string | null;
}) {
  const t = useTranslations("folder-documentation");
  const { createNotification } = useNotification();

  const generateFailedMessage = t.has("notifications.generateFailed" as const)
    ? t("notifications.generateFailed" as const)
    : "Unable to generate canvas from folder assets.";
  const generateNoEligibleAssetsMessage = t.has(
    "notifications.generateNoEligibleAssets" as const,
  )
    ? t("notifications.generateNoEligibleAssets" as const)
    : "No eligible assets in this folder to generate a canvas.";

  const [isGenerating, setIsGenerating] = useState(false);

  const typedIds = useMemo(() => buildTypedIds(assets), [assets]);
  // Eligible folder assets exclude canvases, and are based on
  // the current folder's direct asset list (not subfolder assets).
  const hasEligibleFolderAssetIds =
    typedIds.zapIds.length > 0 ||
    typedIds.tableIds.length > 0 ||
    typedIds.interfaceIds.length > 0 ||
    typedIds.chatbotIds.length > 0 ||
    typedIds.agentIds.length > 0;

  const { setPinnedCanvasAsync } = useSetPinnedCanvas({ folderId });
  const { moveAssetsToFolder } = useMoveAssetsToFolder({ folderId });
  const { mutateAsync: createCanvas } = trpc.canvas.create.useMutation();

  const generateIntoCanvas = useCallback(
    async (canvasId: string) => {
      const response = await fetch(CANVAS_GENERATE_ENDPOINT, {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({
          canvasId,
          ...typedIds,
        }),
      });

      if (!response.ok) {
        let errorPayload: unknown;
        try {
          errorPayload = await response.json();
        } catch {
          // ignore
        }
        console.error("Canvas folder generate failed", {
          status: response.status,
          errorPayload,
        });
        throw new Error(
          `Generate endpoint failed (${response.status}): ${errorPayload ? JSON.stringify(errorPayload) : "<no body>"}`,
        );
      }
    },
    [typedIds],
  );

  const moveCanvasAssetToFolder = useCallback(
    async (canvasId: string) => {
      const canvasAssetId = convertToAssetId(AssetTypes.Canvas, canvasId);
      await new Promise<void>((resolve, reject) => {
        moveAssetsToFolder(
          {
            assetIds: [canvasAssetId],
            destinationFolderId: folderId,
          },
          {
            onSuccess: () => resolve(),
            onError: () => reject(new Error("moveAssetsToFolder failed")),
          },
        );
      });
    },
    [folderId, moveAssetsToFolder],
  );

  const pinCanvas = useCallback(
    async (canvasId: string) => {
      await setPinnedCanvasAsync({ folderId, pinnedCanvasId: canvasId });
    },
    [folderId, setPinnedCanvasAsync],
  );

  const runWithGenerating = useCallback(
    async (operation: () => Promise<string | undefined>) => {
      if (isGenerating) return;
      setIsGenerating(true);

      try {
        return await operation();
      } catch (error) {
        console.error("Failed to generate canvas from folder assets:", error);
        createNotification({
          message: generateFailedMessage,
          type: "warning",
        });
        return;
      } finally {
        setIsGenerating(false);
      }
    },
    [createNotification, generateFailedMessage, isGenerating],
  );

  const updateCanvasWithFolderAssets = useCallback(
    async (canvasId: string) => {
      if (!hasEligibleFolderAssetIds) {
        createNotification({
          message: generateNoEligibleAssetsMessage,
          type: "warning",
        });
        return;
      }

      await generateIntoCanvas(canvasId);
      return canvasId;
    },
    [
      createNotification,
      generateIntoCanvas,
      generateNoEligibleAssetsMessage,
      hasEligibleFolderAssetIds,
    ],
  );

  const generateNewCanvasFromFolderAssets = useCallback(async () => {
    return runWithGenerating(async () => {
      const canvasTitle = folderName?.trim() ?? "";
      const canvas = await createCanvas({ title: canvasTitle });
      await moveCanvasAssetToFolder(canvas.id);
      await pinCanvas(canvas.id);
      return await updateCanvasWithFolderAssets(canvas.id);
    });
  }, [
    createCanvas,
    folderName,
    moveCanvasAssetToFolder,
    pinCanvas,
    runWithGenerating,
    updateCanvasWithFolderAssets,
  ]);

  const regeneratePinnedCanvasFromFolderAssets = useCallback(async () => {
    return runWithGenerating(async () => {
      if (!pinnedCanvasId) return;
      return await updateCanvasWithFolderAssets(pinnedCanvasId);
    });
  }, [pinnedCanvasId, runWithGenerating, updateCanvasWithFolderAssets]);

  return {
    isGeneratingFromFolderAssets: isGenerating,
    hasEligibleFolderAssetIds,
    generateNewCanvasFromFolderAssets,
    regeneratePinnedCanvasFromFolderAssets,
  };
}
